'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import { formatPrice, mergeClassNames } from '@/lib/utils';
import {
  Badge as MokoBadge,
  Button,
  Skeleton,
  toast,
} from '@mokoboko/components';
import { AppearanceSettings } from '@/state/appearance-settings';
import { ClassPack } from '@/state/class-pack-groups';
import { CartData } from '@/lib/types';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { goToCheckout } from '@/helpers/checkout-navigation';
import { classPackCardFlagsOf } from '@/helpers/class-pack';
import { useDetailHref } from '@/contexts/embed';
import { cardImageOf } from '@/lib/entity-image';
import {
  CARD_SIDE_BOX_CLASS,
  CARD_SIDE_BOX_LINES_CLASS,
  CARD_SIDE_BOX_TITLE_CLASS,
  CARD_TITLE_LINK_CLASS,
} from '@/lib/prose';
import { formatValidityPeriod } from '@/helpers/credits';
import { packGuestPassLine } from '@/helpers/guest-pass';
import { DotSeparated } from './dot-separated';
import { FocalPointImage } from './focal-point-image';

interface ClassPackCardProps {
  classPack: ClassPack;
  classPackSettings: AppearanceSettings['class_pack'];
}

export const ClassPackCard = ({
  classPack,
  classPackSettings,
}: ClassPackCardProps) => {
  const router = useRouter();
  const detailHref = useDetailHref(
    'pack',
    classPack.class_pack_id,
    classPack.slug
  );

  const isPopular = classPack.is_popular;
  const flags = classPackCardFlagsOf(classPackSettings);
  const { url: cardImageUrl, focalX, focalY } = cardImageOf(classPack);
  const showImage = !!cardImageUrl && flags.showImage;
  const guestPassLine = packGuestPassLine(classPack.guest_passes);

  const handleCardClick = () => {
    router.push(detailHref);
  };

  const handleCheckout = () => {
    const cartData: CartData<'class-pack'> = {
      items: [
        {
          ...classPack,
          quantity: 1,
          price: classPack.price ?? 0,
        },
      ],
      totalAmount: classPack.price ?? 0,
      checkoutType: 'class-pack',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: classPack.name,
        content_type: 'class_pack',
        content_ids: [classPack.slug],
        value: classPack.price ?? 0,
        currency: classPack.currency,
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'class_pack',
        product_name: classPack.name,
        product_id: classPack.class_pack_id,
        price: classPack.price ?? null,
        currency: classPack.currency ?? null,
        quantity: 1,
      },
    });
  };

  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div className={mergeClassNames(CARD_SIDE_BOX_CLASS, 'md:w-[140px]')}>
        <div className={CARD_SIDE_BOX_LINES_CLASS}>
          {classPack?.total_number_of_classes != null && (
            <p className={CARD_SIDE_BOX_TITLE_CLASS}>
              {classPack.total_number_of_classes > 1
                ? `${classPack.total_number_of_classes} classes`
                : `${classPack.total_number_of_classes} class`}
            </p>
          )}
          {flags.showValidity && classPack?.expiry_days != null && (
            <p className="text-sm text-gray-700">
              <span className="hidden md:inline">Valid </span>
              {formatValidityPeriod(classPack.expiry_days, { short: true })}
            </p>
          )}
        </div>
        {flags.showPrice && (
          <div className="md:hidden">
            <MokoBadge
              label={formatPrice(classPack?.price, classPack?.currency)}
              color="gray"
              size="md"
              className="bg-gray-200"
            />
          </div>
        )}
      </div>

      <div
        className="group flex w-full cursor-pointer overflow-hidden rounded-xl border border-gray-200 shadow-xs transition-shadow duration-200 max-md:flex-col md:hover:shadow-lg"
        onClick={handleCardClick}
      >
        {isPopular && (
          <MokoBadge
            label="Most popular"
            color="red"
            size="md"
            className="h-7 w-full justify-center rounded-b-none rounded-t-[11px] border-0 bg-popular-badge-fill px-4 py-1 text-popular-badge-ink md:hidden"
          />
        )}
        <div
          className={mergeClassNames(
            'flex w-full flex-col gap-5 p-4 md:gap-6 md:p-5',
            !flags.showDescription ||
              (!classPack?.short_description && !classPack?.description)
              ? 'md:gap-5'
              : ''
          )}
        >
          <div className="flex flex-col gap-2 md:hidden">
            <Link
              href={detailHref}
              className={mergeClassNames(
                CARD_TITLE_LINK_CLASS,
                'group-hover:text-red-500'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {classPack?.name}
            </Link>
            {guestPassLine && (
              <span className="text-sm text-gray-700">{guestPassLine}</span>
            )}
            {flags.showDescription && classPack?.short_description && (
              <p className="text-sm text-gray-700">
                {classPack.short_description}
              </p>
            )}
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-center gap-2.5">
                <Link
                  href={detailHref}
                  className={mergeClassNames(
                    CARD_TITLE_LINK_CLASS,
                    'group-hover:text-red-500'
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {classPack?.name}
                </Link>
                {classPack.is_popular && (
                  <MokoBadge
                    label="Most popular"
                    color="red"
                    size="sm"
                    className="bg-popular-badge-fill text-popular-badge-ink"
                  />
                )}
              </div>
              <DotSeparated
                parts={[
                  flags.showPrice &&
                    formatPrice(classPack?.price, classPack?.currency),
                  guestPassLine,
                ].filter(Boolean)}
              />
            </div>
            <div className="shrink-0">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckout();
                }}
                className="group-hover:bg-red-500"
              >
                Buy pack
              </Button>
            </div>
          </div>

          {flags.showDescription && classPack?.short_description && (
            <p className="hidden text-sm text-gray-700 md:block">
              {classPack.short_description}
            </p>
          )}

          {showImage && (
            <div className="relative h-[200px] w-full overflow-hidden rounded-xl md:hidden">
              <FocalPointImage
                src={cardImageUrl!}
                focalPointX={focalX}
                focalPointY={focalY}
              />
            </div>
          )}

          <div className="w-full md:hidden">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleCheckout();
              }}
              fullWidth
              className="group-hover:bg-red-500"
            >
              Buy pack
            </Button>
          </div>
        </div>
        {showImage && (
          <div
            className={mergeClassNames(
              'max-md:pb-1/2 relative hidden h-[200px] w-auto shrink-0 rounded-l-xl max-md:w-full md:block md:h-[100px] md:w-[100px]',
              flags.showDescription &&
                (classPack?.short_description || classPack?.description)
                ? 'h-full w-[156px] md:h-full md:w-[156px]'
                : ''
            )}
          >
            <FocalPointImage
              src={cardImageUrl!}
              focalPointX={focalX}
              focalPointY={focalY}
              className="absolute"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const ClassPackCardSkeleton = () => {
  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div className={mergeClassNames(CARD_SIDE_BOX_CLASS, 'md:w-[140px]')}>
        <div className={CARD_SIDE_BOX_LINES_CLASS}>
          <span className="md:hidden">
            <Skeleton text="body-sm" width={72} />
          </span>
          <span className="hidden md:block">
            <Skeleton text="body-lg" width={72} />
          </span>
          <Skeleton text="body-sm" width={88} />
        </div>
        <div className="md:hidden">
          <Skeleton height={24} width={60} />
        </div>
      </div>

      <div className="flex w-full overflow-hidden rounded-xl border border-gray-200 shadow-xs max-md:flex-col">
        <div className="flex w-full flex-col gap-5 p-4 md:gap-6 md:p-5">
          <div className="flex w-full flex-col gap-2 md:hidden">
            <Skeleton text="body-lg" width={176} />
            <Skeleton text="body-sm" className="w-3/4" />
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton text="body-lg" width={176} />
              <Skeleton text="body-sm" width={72} />
            </div>
            <Skeleton isRounded height={40} width={104} className="shrink-0" />
          </div>

          <div className="hidden w-full md:block">
            <Skeleton text="body-sm" className="w-3/4" />
          </div>

          <Skeleton isRounded height={200} width="100%" className="md:hidden" />

          <Skeleton isRounded height={40} width="100%" className="md:hidden" />
        </div>

        <Skeleton
          height="100%"
          width={156}
          className="hidden shrink-0 self-stretch !rounded-none md:block"
        />
      </div>
    </div>
  );
};
